import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import CustomLoader from "../custom/CustomLoader";
import PaginationControl from "../custom/PaginationControl";
import moment from "moment";
import { useFetch, useMutate } from "../../hooks/queries";
import { FaTimes, FaDownload } from "react-icons/fa";

const UserDataModal = ({
  show,
  onHide,
  userType, // "students" or "teachers"
  statusFilter, // "logged" or "notLogged"
  startDate,
  endDate,
  schoolId,
  operatorId,
}) => {
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Reset page when modal opens or filters change
  useEffect(() => {
    if (show) {
      setPage(1);
    }
  }, [show, statusFilter, startDate, endDate, schoolId, operatorId]);

  const handleSort = (column, sortDirection) => {
    const columnFieldMap = {
      canvasId: "CanvasId",
      name: "Name",
      email: "Email",
      canvasSchool: "CanvasSchool",
      schoolName: "SchoolName",
      grade: "Grade",
      lastLogin: "LastLogin",
    };
    setSortBy(columnFieldMap[column.id]);
    setSortOrder(sortDirection);
    setPage(1);
  };

  const buildRequestBody = () => {
    const ot = localStorage.getItem("OTsReD");
    const st = localStorage.getItem("STsReD");

    const safeParseInt = (value) => {
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const parsedOt = safeParseInt(ot);
    const parsedSt = safeParseInt(st);

    const data = {
      OperatorId: 0,
      SchoolId: 0,
      ...(statusFilter !== "all" && {
        StartDate: startDate,
        EndDate: endDate,
      }),
      PageNumber: page,
      PageSize: perPage,
      SearchText: "",
      SortBy: sortBy,
      SortOrder: sortOrder,
    };

    // Only add IsLoggedIn filter if not showing all records
    if (statusFilter !== "all") {
      data.IsLoggedIn = statusFilter === "logged";
    }

    if (parsedOt !== 0 && parsedSt === 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = safeParseInt(schoolId);
    } else if (parsedOt !== 0 && parsedSt !== 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = parsedSt;
    } else {
      data.OperatorId = safeParseInt(operatorId);
      data.SchoolId = safeParseInt(schoolId);
    }

    return data;
  };

  const apiUrl =
    userType === "students"
      ? `${process.env.REACT_APP_API_URL}CanvasUsers/GetStudents`
      : `${process.env.REACT_APP_API_URL}CanvasUsers/GetTeachers`;

  const {
    data: usersResponse,
    isPending,
    isLoading,
    isFetching,
  } = useFetch({
    queryKey: [
      `${userType}Modal`,
      page,
      perPage,
      startDate,
      endDate,
      schoolId,
      operatorId,
      sortBy,
      sortOrder,
      statusFilter,
    ],
    url: apiUrl,
    method: "PATCH",
    data: buildRequestBody(),
    enabled: show,
  });

  const users = usersResponse?.data || [];
  const totalRows = usersResponse?.totalCount || 0;
  const isLoading_ = isPending || isLoading || isFetching;

  const columns =
    userType === "students"
      ? [
          {
            id: "canvasId",
            name: "Canvas Id",
            selector: (row) => row.canvasId,
            width: "130px",
            sortable: true,
          },
          {
            id: "name",
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
          },
          {
            id: "email",
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
          },
          {
            id: "canvasSchool",
            name: "Canvas School",
            selector: (row) => row.canvasSchool,
            sortable: true,
          },
          {
            id: "schoolName",
            name: "CSLP School",
            selector: (row) => row.schoolName,
            sortable: true,
          },
          {
            id: "grade",
            name: "Grade",
            selector: (row) => row.grade,
            sortable: true,
            width: "90px",
          },
          {
            id: "lastLogin",
            name: "Last Login Date",
            selector: (row) =>
              row.lastLogin == null
                ? "N/A"
                : moment(row.lastLogin).format("YYYY-MM-DD"),
            sortable: true,
          },
        ]
      : [
          {
            id: "canvasId",
            name: "Canvas Id",
            selector: (row) => row.canvasId,
            width: "130px",
            sortable: true,
          },
          {
            id: "name",
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
          },
          {
            id: "email",
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
          },
          {
            id: "canvasSchool",
            name: "Canvas School",
            selector: (row) => row.canvasSchool,
            sortable: true,
          },
          {
            id: "schoolName",
            name: "CSLP School",
            selector: (row) => row.schoolName,
            sortable: true,
          },
          {
            id: "lastLogin",
            name: "Last Login Date",
            selector: (row) =>
              row.lastLogin == null
                ? "N/A"
                : moment(row.lastLogin).format("YYYY-MM-DD"),
            sortable: true,
          },
        ];

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const buildExportPayload = () => {
    const base = {
      schoolId: schoolId == null ? 0 : schoolId,
      operatorId: operatorId == null ? 0 : operatorId,
    };

    if (statusFilter === "all") {
      return base;
    }

    return {
      ...base,
      startDate: startDate,
      endDate: endDate,
      IsLoggedIn: statusFilter === "logged",
    };
  };

  const downloadFile = (data, fileName) => {
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.download = fileName;
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportUrl =
    userType === "students"
      ? `${process.env.REACT_APP_API_URL}CanvasUsers/ExportStudents`
      : `${process.env.REACT_APP_API_URL}CanvasUsers/ExportTeachers`;

  const exportMutation = useMutate({
    mutationKey: [`export${userType}Modal`],
    url: exportUrl,
    method: "PATCH",
    axiosConfig: {
      responseType: "arraybuffer",
    },
    onSuccess: (data) => {
      const fileName = `${modalTitle}.xlsx`;
      downloadFile(data, fileName);
    },
  });

  const exporting = exportMutation.isPending || exportMutation.isLoading;

  const modalTitle =
    userType === "students"
      ? statusFilter === "logged"
        ? "Students - Logged In"
        : statusFilter === "notLogged"
          ? "Students - Not Logged In"
          : "Students - All"
      : statusFilter === "logged"
        ? "Teachers - Logged In"
        : statusFilter === "notLogged"
          ? "Teachers - Not Logged In"
          : "Teachers - All";

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header
        style={{
          backgroundColor: "#287a9f",
          color: "white",
          borderRadius: "0",
        }}
      >
        <Modal.Title style={{ fontSize: "18px", fontWeight: "bold" }}>
          {modalTitle}
        </Modal.Title>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={onHide}
          aria-label="Close"
        ></button>
      </Modal.Header>
      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "15px", borderBottom: "1px solid #ddd" }}>
          <button
            className="btn cts-btn-primary-3 btn-sm"
            onClick={() => exportMutation.mutate(buildExportPayload())}
            disabled={exporting || isLoading_}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <FaDownload />
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
        <DataTable
          data={users}
          columns={columns}
          progressPending={isLoading_}
          progressComponent={<CustomLoader />}
          onSort={handleSort}
          sortServer
          fixedHeader
          fixedHeaderScrollHeight={"50vh"}
          defaultSortFieldId={1}
          noDataComponent={
            <div style={{ padding: "24px", textAlign: "center" }}>
              No records found
            </div>
          }
        />
        <PaginationControl
          totalCount={totalRows}
          currentPage={page}
          pageSize={perPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePerRowsChange}
          pageSizeOptions={[20, 50, 100, 200]}
          disabled={isLoading_}
        />
      </Modal.Body>
    </Modal>
  );
};

export default UserDataModal;
